import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row, Select } from 'antd';
import Banner from '../Banner/Banner';
import Modal from '../Modal/Modal';
import styles from './index.module.less';
import { TFunction } from 'i18next';
import { Button } from '../Button/Button';
import { CancellationReason } from '../../api/dtos/deliveries';

type CancelDeliveryModalProps = {
  isVisible: boolean;
  onConfirm: (reason: CancellationReason) => void;
  onClose?: (e: React.SyntheticEvent) => void;
};

const renderReasonOptionsList = (t: TFunction): React.ReactNode[] => {
  return Object.values(CancellationReason).map((reasonCode: CancellationReason) => (
    <Select.Option key={`reason-option-${reasonCode}`} data-testid={`reasonSelectItem`} value={reasonCode}>
      {t(`cancellationReasons.${reasonCode}`)}
    </Select.Option>
  ));
};

const CancelDeliveryModal = ({ isVisible, onClose, onConfirm }: CancelDeliveryModalProps) => {
  const { t } = useTranslation();
  const renderReasons = useCallback(() => {
    return renderReasonOptionsList(t);
  }, [t]);
  const [reason, setReason] = useState<CancellationReason>();

  const onConfirmHandler = useCallback(() => {
    reason && onConfirm(reason);
  }, [reason]);

  const onChangeReasonHandler = (reason: CancellationReason) => {
    setReason(reason);
  };
  const renderedReasonOptions = renderReasons();
  return (
    <Modal
      visible={isVisible}
      onClose={onClose}
      className={styles.cancelDeliveryModal}
      titleText={t('modal.cancelDelivery.title')}
      footer={
        <Row gutter={[12, 12]} justify="end">
          <Col xs={24} sm={10} md={8}>
            <Button block onClick={onClose} className={styles.buttonDiscard} data-testid="abortCancelDeliveryButton">
              {t('modal.cancelDelivery.discardButtonText')}
            </Button>
          </Col>
          <Col xs={24} sm={12} md={10}>
            <Button
              danger
              type={'primary'}
              block
              onClick={onConfirmHandler}
              disabled={!reason}
              data-testid="confirmCancelDeliveryButton"
              className={styles.buttonConfirm}
            >
              {t('modal.cancelDelivery.confirmButtonText')}
            </Button>
          </Col>
        </Row>
      }
    >
      <div data-testid="cancelDeliveryModalWrapper">
        <Form layout="vertical" hideRequiredMark>
          <Row>
            <Col xs={24} md={24} lg={24}>
              <Banner
                message={
                  <div className={styles.bannerContent}>
                    <div className={styles.bannerTitle}>{t('modal.cancelDelivery.bannerTitle')} </div>
                    <div className={styles.bannerText}>{t('modal.cancelDelivery.bannerText')}</div>
                  </div>
                }
                type={'error'}
                className={styles.bannerCancellation}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={24} md={24} lg={24}>
              <Form.Item label={t('modal.cancelDelivery.selectReasonTitle')}>
                <Select
                  placeholder={t('modal.cancelDelivery.selectReasonPlaceholder')}
                  data-testid="cancellationReasonSelect"
                  onChange={onChangeReasonHandler}
                >
                  {renderedReasonOptions}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  );
};

export default CancelDeliveryModal;
